import { getFieldId, getLegacySubfieldId, getReviewId } from './utils'
import { DROPDOWN_DEFAULT_VALUE } from '../../../utility/constants'

export const syncWithParent = (
  childFieldId,
  fieldTemplateDep,
  listDataDep,
  allValues,
  listData,
  teamsList,
  other = {}
) => {
  // Initialize options with the original listData
  let options = [...listData]

  // Helper to normalize any scalar/unset value to an array
  const normalizeToArray = (val) =>
    Array.isArray(val)
      ? val
      : val === undefined || val === null || val === '' || val === DROPDOWN_DEFAULT_VALUE
      ? []
      : [val]

  // Compute allowed parent IDs from any selected grandparents of the given parent field
  const getAllowedParentIdsFromGrandparents = (parentId, relObj) => {
    const userTeamMap = other?.userTeamMap || []
    // grandparents are relations where this parent is the child
    const gpRels = fieldTemplateDep?.filter((it) => it.child_field_template_id === parentId) || []

    let hasAnyGpSelected = false
    let intersectionSet = null

    gpRels.forEach((gpRel) => {
      const gpId = gpRel?.parent_field_template_id
      const gpVals = normalizeToArray(
        allValues[getFieldId(gpId)] || (other?.allValues && other?.allValues[getFieldId(gpId)])
      )
      if (gpVals.length === 0) {
        return
      }
      hasAnyGpSelected = true

      // Determine which parent IDs are valid given this grandparent selection
      let allowedParentsForThisGp = []
      if (
        gpRel?.parent_dynamic_field_type?.type === 'teams' &&
        relObj?.parent_dynamic_field_type?.type === 'users'
      ) {
        // Teams -> Users mapping via userTeamMap
        const allowedUserIds = userTeamMap
          ?.filter((it) => gpVals.includes(it.team_id))
          ?.map((it) => it.user_id)
        allowedParentsForThisGp = allowedUserIds || []
      } else {
        // Generic mapping via listDataDep
        const rows = listDataDep?.filter((it) => gpVals.includes(it.parent_id)) || []
        allowedParentsForThisGp = rows.map((it) => it.child_id)
      }

      const currentSet = new Set(allowedParentsForThisGp)
      if (intersectionSet == null) {
        intersectionSet = currentSet
      } else {
        // intersect with previous grandparents
        intersectionSet = new Set([...intersectionSet].filter((id) => currentSet.has(id)))
      }
    })

    return hasAnyGpSelected ? intersectionSet || new Set() : null
  }

  // From allowed parent IDs, compute allowed child IDs for the current child field
  const getAllowedChildIdsFromAllowedParents = (allowedParentIds, relObj) => {
    const userTeamMap = other?.userTeamMap || []
    if (
      relObj?.parent_dynamic_field_type?.type === 'teams' &&
      other?.field?.dynamic_field_type?.type === 'users'
    ) {
      // Parent is teams, child is users
      const allowedUserIds = userTeamMap
        ?.filter((it) => allowedParentIds.has(it.team_id))
        ?.map((it) => it.user_id)
      return new Set(allowedUserIds || [])
    }
    const rows = listDataDep?.filter((it) => allowedParentIds.has(it.parent_id)) || []
    return new Set(rows.map((it) => it.child_id))
  }

  // Apply grandparent-based pre-filter when parent is unset (only for single-parent scenarios)
  const applyGrandparentPrefilter = (parentId, relObj) => {
    const relCountForChild = fieldTemplateDep?.filter(
      (it) => it.child_field_template_id === relObj.child_field_template_id
    )?.length
    // Only apply when child has exactly one parent relation
    if (relCountForChild !== 1) return

    const allowedParents = getAllowedParentIdsFromGrandparents(parentId, relObj)
    if (!allowedParents || allowedParents.size === 0) return

    const allowedChildIds = getAllowedChildIdsFromAllowedParents(allowedParents, relObj)
    if (allowedChildIds && allowedChildIds.size > 0) {
      options = options.filter((opt) => allowedChildIds.has(opt.id))
    }
  }

  // Find all parent relations for the given childFieldId
  const relArray = fieldTemplateDep?.filter((it) => it.child_field_template_id === childFieldId)

  // Iterate over each parent relation
  relArray.forEach((relObj) => {
    if (relObj) {
      const parentId = relObj?.parent_field_template_id
      // Get the value of the parent
      const value =
        allValues[getFieldId(parentId)] ||
        (other?.allValues && other?.allValues[getFieldId(parentId)])

      // Filter options for the child based on the parent value
      // ⚠️ other?.field is child field itself

      if (
        relObj?.parent_dynamic_field_type?.type == 'teams' &&
        other?.field?.dynamic_field_type?.type == 'users'
      ) {
        // If optional parent is unset, skip constraining options
        const parentUnset =
          (Array.isArray(value) && value.length === 0) ||
          (!Array.isArray(value) &&
            (value === undefined ||
              value === null ||
              value === '' ||
              value === DROPDOWN_DEFAULT_VALUE))
        if (parentUnset) {
          applyGrandparentPrefilter(parentId, relObj)
          return
        }
        const userIds = other?.userTeamMap
          ?.filter((it) =>
            Array.isArray(value) ? value?.includes(it.team_id) : value == it.team_id
          )
          ?.map((it) => it.user_id)
        // INTERSECTION with existing options to support multi-parent constraints
        options = options.filter((it) => userIds?.includes(it.id))
      } else {
        // Check if the parent value is an array (multi-value)

        if (Array.isArray(value)) {
          // If optional parent is unset (empty array), skip constraining
          if (value.length === 0) {
            applyGrandparentPrefilter(parentId, relObj)
            return
          }
          // Collect all relation rows for ALL parent values
          const relationRows = listDataDep.filter((it) => value.includes(it.parent_id))

          if (relationRows.length > 0) {
            const filteredIds = relationRows.map((it) => it.child_id)
            // INTERSECTION with existing options to support multi-parent constraints
            options = options.filter((opt) => filteredIds.includes(opt.id))
          } else {
            options = []
          }
        } else {
          // If optional parent is unset, skip constraining
          const parentUnset =
            value === undefined ||
            value === null ||
            value === '' ||
            value === DROPDOWN_DEFAULT_VALUE
          if (parentUnset) {
            applyGrandparentPrefilter(parentId, relObj)
            return
          }
          const relationRows = listDataDep.filter((it) => it.parent_id === value)

          if (relationRows.length > 0) {
            const filteredIds = relationRows.map((it) => it.child_id)

            // INTERSECTION with existing options to support multi-parent constraints
            options = options.filter((opt) => filteredIds.includes(opt.id))
          } else {
            options = []
          }
        }
      }
    }
  })

  return options
}

const getTableIdFromTableStructure = (tableStructure, columnId) => {
  for (const [key, value] of Object.entries(tableStructure)) {
    // here, we have columns and rows in object named value
    // check if childId is present in columns
    // if yes -> return key
    const isPresent = value?.columns?.find((it) => it.id == columnId)
    // return isPresent ? { key, rows: value?.rows } : null
    if (isPresent) {
      return { key, rows: value?.rows }
    }
  }
  return null
}

/* It just sync immediate child */
export const syncChild = (
  parentFieldId,
  fieldTemplateDep,
  listDataDep,
  allValues,
  teamsList,
  other
) => {
  // Helper: pick correct source list
  const getSourceList = (listType) => (listType === 'team' ? teamsList : other?.listData || [])

  // Find the child relation
  const relObj = fieldTemplateDep?.find((it) => it.parent_field_template_id == parentFieldId)

  if (!relObj) return allValues

  const childId = relObj?.child_field_template_id

  // Find matching relation rows in listDataDep for given parent & child values
  let parentValue = allValues[getFieldId(parentFieldId)]
  let childValue = allValues[getFieldId(childId)]

  // Get all relations for this parent & child (this gives us parent/child list types)
  const relationRows = listDataDep.filter(
    (it) => it.parent_id == parentValue && it.child_id == childValue
  )

  const table = getTableIdFromTableStructure(other?.tableStructure, childId)

  // Validate child against ALL of its parents (multi-parent support)
  const allParentRels =
    fieldTemplateDep?.filter((it) => it.child_field_template_id == childId) || []
  const userTeamMap = other?.userTeamMap || []

  const toArray = (val) =>
    Array.isArray(val)
      ? val
      : val === undefined || val === null || val === DROPDOWN_DEFAULT_VALUE
      ? []
      : [val]

  const childValidForAllParents = allParentRels.every((rel) => {
    const pVals = toArray(allValues[getFieldId(rel.parent_field_template_id)])
    // If a parent is unset, it does not constrain the child
    if (pVals.length === 0) return true

    // Valid if there exists at least one mapping for this parent to the current child
    const validViaListDep = pVals.some((p) =>
      listDataDep.some((row) => row.parent_id == p && row.child_id == childValue)
    )

    // Also consider team -> user mapping if available
    const validViaTeamUser =
      rel?.parent_dynamic_field_type?.type == 'teams' && userTeamMap?.length
        ? pVals.some((teamId) =>
            userTeamMap.some((m) => m.team_id == teamId && m.user_id == childValue)
          )
        : false

    return validViaListDep || validViaTeamUser
  })

  // No valid relation found → delete/reset child value
  const parentUnset =
    (Array.isArray(parentValue) && parentValue.length === 0) ||
    (!Array.isArray(parentValue) &&
      (parentValue === undefined ||
        parentValue === null ||
        parentValue === '' ||
        parentValue === DROPDOWN_DEFAULT_VALUE))
  if ((!relationRows.length && !parentUnset) || !childValidForAllParents) {
    if (allValues[getFieldId(childId)]) {
      delete allValues[getFieldId(childId)]
    } else if (table && other?.allValueUpdated && other?.allValueUpdated[table.key]) {
      const tableData = other?.allValueUpdated[table.key]

      // delete from table structured data
      for (const [key, value] of Object.entries(tableData)) {
        delete value[getFieldId(childId)]
      }

      // delete from allValue for each row
      table.rows.forEach((row) => {
        delete other?.allValueUpdated[getLegacySubfieldId(childId, row)]
      })

      return other?.allValueUpdated
    }

    return allValues
  }

  // If a relation exists, also ensure the IDs match list types
  if (!relationRows.length) {
    return allValues
  }
  const { parent_list_type, child_list_type } = relationRows[0]
  const parentSource = getSourceList(parent_list_type)
  const childSource = getSourceList(child_list_type)

  const validParent = parentSource.some((item) => item.id === parentValue)
  const validChild = childSource.some((item) => item.id === childValue)

  if (!validParent || !validChild) {
    delete allValues[getFieldId(childId)]
  }

  return allValues
}

/* It recursively sync all children down the line in hierarchy */
export const syncChildRecursively = (
  fields = [],
  fieldTemplateDep,
  listDataDep,
  userTeamMap,
  allValues
) => {
  let updatedAllValues = { ...allValues }
  // find child for all field and update their value accordingly

  const normalizeToArray = (val) =>
    Array.isArray(val)
      ? val
      : val === undefined || val === null || val === '' || val === DROPDOWN_DEFAULT_VALUE
      ? []
      : [val]

  fields.forEach((field) => {
    const relObjs = fieldTemplateDep?.filter((it) => it.parent_field_template_id == field.id) || []

    relObjs.forEach((relObj) => {
      if (relObj) {
        const childId = relObj?.child_field_template_id
        const cField = fields.find((f) => f.id == childId)

        const parentVals = normalizeToArray(updatedAllValues[getFieldId(field.id)])
        const childVals = normalizeToArray(updatedAllValues[getFieldId(childId)])

        // If this parent is unset (optional), it imposes no constraint on the child.
        if (parentVals.length === 0) {
          return
        }

        let isValidForThisParent = false

        if (
          field.dynamic_field_type?.type == 'teams' &&
          cField.dynamic_field_type?.type == 'users'
        ) {
          isValidForThisParent = parentVals.some((p) =>
            childVals.some((c) => userTeamMap.some((it) => it.team_id == p && it.user_id == c))
          )
        } else {
          isValidForThisParent = parentVals.some((p) =>
            childVals.some((c) => listDataDep.some((it) => it.parent_id == p && it.child_id == c))
          )
        }

        // If invalid for this set parent, reset child
        if (!isValidForThisParent) {
          updatedAllValues[getFieldId(childId)] = DROPDOWN_DEFAULT_VALUE
        }
      }
    })
  })

  return updatedAllValues
}

/* It recursively sync all children down the line in hierarchy - review */
export const syncChildParentReviewRecursively = (id, newValue, fieldTemplateDep, allValues) => {
  // if(value == true) find all parent and set true - if
  // if(value == false) find all child and set false - else
  let syncedReviewValues = { ...allValues }
  if (newValue) {
    // newValue == true
    let currentChildId = id
    while (currentChildId >= 0) {
      const parentOfCurrentChild = fieldTemplateDep.find(
        (it) => it.child_field_template_id == currentChildId
      )

      if (parentOfCurrentChild) {
        syncedReviewValues = {
          ...syncedReviewValues,
          [getReviewId(parentOfCurrentChild.parent_field_template_id)]: true,
        }
        currentChildId = parentOfCurrentChild.parent_field_template_id
      } else {
        currentChildId = -1
      }
    }
  } else {
    // newValue == false
    let currentParentIds = [id]
    let currentIndex = 0
    while (currentParentIds.length > 0 && currentIndex < currentParentIds.length) {
      let parentIds = []
      currentParentIds.forEach((it) => {
        const childOfCurrentParent = fieldTemplateDep.filter(
          (ele) => ele.parent_field_template_id == it
        )
        if (childOfCurrentParent.length > 0) {
          childOfCurrentParent.forEach((rel) => {
            syncedReviewValues = {
              ...syncedReviewValues,
              [getReviewId(rel.child_field_template_id)]: false,
            }
            parentIds.push(rel.child_field_template_id)
          })
        }
      })
      currentParentIds = parentIds
      currentIndex = 0
    }
  }
  return syncedReviewValues
}

const arrayCompare = (_arr1, _arr2) => {
  if (!Array.isArray(_arr1) || !Array.isArray(_arr2) || _arr1.length !== _arr2.length) {
    return false
  }

  // .concat() to not mutate arguments
  const arr1 = _arr1.concat().sort()
  const arr2 = _arr2.concat().sort()

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false
    }
  }

  return true
}

const getChildFieldId = (parentId, fieldTemplateDep) => {
  return fieldTemplateDep?.find((item) => item?.parent_field_template_id == parentId)
    ?.child_field_template_id
}

const getParentSame = (parentId, child_id, listDataDep, targetValue) => {
  const isPresentSame = listDataDep.find(
    (item) => targetValue.includes(item.parent_id) && item.child_id == child_id
  )

  return isPresentSame
  // return filteredlistDataDep.find((item) => item.child_id == child_id)
}

/**
 * We are trying deep driving child and update the value if needed
 * @param {*} param0
 */
export const syncChildForListMulti = ({
  allValues,
  fieldId,
  targetValue,
  listDataDep,
  fieldTemplateDep,
}) => {
  const preFieldValue = allValues[fieldId]
  const child = {}
  // validating if field value has changed, As this is mutli so will validate in array
  if (!arrayCompare(preFieldValue, targetValue)) {
    //if parent has child
    let childId = getChildFieldId(fieldId, fieldTemplateDep)
    const loopField = ({
      _parentId,
      fieldTemplateDep,
      child,
      _childId,
      allValues,
      targetValue,
    }) => {
      // when child does not have childField populated
      if (!child[_childId]) {
        const childFieldData = allValues[getFieldId(_childId)]
        // checking if child filed has some data selected already
        if (childFieldData !== DROPDOWN_DEFAULT_VALUE && Array.isArray(childFieldData)) {
          const updatedChild = childFieldData.filter((item) => {
            // Updating child fild with Empty value
            if (targetValue == DROPDOWN_DEFAULT_VALUE) return false

            const isSameParent = getParentSame(_parentId, item, listDataDep, targetValue)

            return isSameParent
          })
          if (updatedChild?.length) {
            child[getFieldId(_childId)] = updatedChild
          } else {
            child[getFieldId(_childId)] = DROPDOWN_DEFAULT_VALUE
          }
        }
        const childFiledId = getChildFieldId(_childId, fieldTemplateDep)
        // checking if next child is present and upcoming parent has some data
        if (childFiledId && allValues[getFieldId(_childId)] !== DROPDOWN_DEFAULT_VALUE) {
          loopField({
            _parentId: _childId,
            fieldTemplateDep,
            child,
            _childId: childFiledId,
            targetValue: child[getFieldId(_childId)],
            allValues,
          })
        }
        return
      }
      return
    }
    loopField({
      _parentId: fieldId,
      fieldTemplateDep,
      child,
      _childId: childId,
      allValues,
      targetValue,
    })
    return child
  }
}
